/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const { FuncTerm } = require('@cedalo/parser');
const JSG = require('../../JSG');
const Expression = require('../expr/Expression');
const Strings = require('../../commons/Strings');

const DPL_NAME_EXCEPTIONS = ['SELECT', 'BAR', 'CELLCHART', 'READ', 'WRITE'];

/**
 * Class to contain cell information for the worksheet.
 *
 * @class Cell
 * @constructor
 */
module.exports = class Cell {
	constructor(expr) {
		this._expr = expr;
		this._value = undefined;
		this._hasErrorValue = false;
	}

	newInstance() {
		return new Cell();
	}

	copy() {
		const copy = this.newInstance();

		if (this._expr) {
			const value = this._expr._value;
			copy._expr = this._expr.copy();
			copy._expr._value = value;
		}
		copy._value = this._value;
		copy._values = this._values;

		if (this.format) {
			copy.format = {};
			Object.assign(copy._properties.format, this.format);
		}
		if (this.textFormat) {
			copy.textFormat = {};
			Object.assign(copy._properties.textFormats, this.textFormat);
		}
		if (this.attributes) {
			copy.attributes = {};
			Object.assign(copy._properties.attributes, this.attributes);
		}

		return copy;
	}

	get properties() {
		return this._properties;
	}

	set properties(properties) {
		this._properties = properties;
	}

	get format() {
		return this._properties ? this._properties.formats : undefined;
	}

	set format(format) {
		if (!this._properties) {
			this._properties = {};
		}
		this._properties._formats = format;
	}

	get textFormat() {
		return this._properties ? this._properties.textFormats : undefined;
	}

	set textFormat(format) {
		if (!this._properties) {
			this._properties = {};
		}
		this._properties._textFormats = format;
	}

	get attributes() {
		return this._properties ? this._properties.attributes : undefined;
	}

	set attributes(attributes) {
		if (!this._properties) {
			this._properties = {};
		}
		this._properties._attributes = attributes;
	}

	get value() {
		return this._value;
	}

	getValue() {
		return this._value;
	}

	setTargetValue(value) {
		this._targetValue = value;
	}

	getTargetValue() {
		return this._targetValue;
	}

	toLocaleString(sheet, encode) {
		const expr = this.getExpression();

		if (expr === undefined) {
			return '';
		}

		let value;
		if (expr.hasFormula()) {
			// DL-1592: if expression has a formula but not term => there was an error while parsing, so simply use formula
			value = expr.hasTerm()
				? expr.toLocaleString(JSG.getParserLocaleSettings(), { sheet, useName: true })
				: `=${expr.getFormula()}`;
		} else {
			value = expr.toLocaleString(JSG.getParserLocaleSettings(), {
				sheet,
				useName: true
			});
			// value = this.getValue();
		}

		if (encode) {
			value = Strings.encodeXML(String(value));
		}

		return value;
	}

	setValue(value) {
		if (value !== '#CALC') {
			this._value = value;
			this._hasErrorValue = `${value}`.startsWith('#');
		}
	}

	setInfo(info) {
		if (this._info && this._info.keep) {
			return;
		}
		this._info = info;
	}

	get hasErrorValue() {
		return this._hasErrorValue;
	}

	get errorInfo() {
		return this._info && this._info.error;
	}

	get rawtype() {
		return this._info && this._info.rawtype;
	}

	get values() {
		return this._info ? this._info.values : undefined;
	}

	get valuesMarker() {
		return this._info ? this._info.marker : undefined;
	}

	get xvalue() {
		return this._info ? this._info.xvalue : 'time';
	}

	clearContent() {
		this._value = undefined;
		this._expr = undefined;
		this._info = undefined;
	}

	clearFormat() {
		this._properties = undefined;
	}

	clearFormula() {
		if (this._expr) {
			this._expr.setFormula(undefined);
			this._expr.setTerm(undefined);
		}
	}

	hasFormula() {
		return this._expr && this._expr.hasFormula();
	}

	hasFormat() {
		return this._properties !== undefined;
	}

	hasContent() {
		return this._expr !== undefined;
	}

	hasHiddenPayload() {
		const term = this.getTerm();
		// DL-6005:
		if (term) {
			const showName = FuncTerm.showName(term);
			const showValueMarker = FuncTerm.showValueMarker(term);
			if (showName && showValueMarker && !DPL_NAME_EXCEPTIONS.includes(term.getFuncId())) {
				const value = this.value;
				return value !== true && value !== false && `${value}`[0] !== '#';
			}
		}
		return false;
	}

	clearAll() {
		this.clearFormat();
		this.clearContent();
	}

	getTerm() {
		return this._expr ? this._expr.getTerm() : undefined;
	}

	getFormula() {
		return this._expr && this._expr.hasFormula() ? this._expr.getFormula() : undefined;
	}

	getExpression() {
		return this._expr;
	}

	setExpression(expr) {
		this._expr = expr;
		this._expr._isDirty = true;
		if (this._expr && this._expr.hasFormula() === false) {
			this._value = this._expr.getValue();
		} else {
			this._value = undefined;
		}
	}

	evaluate(sheet) {
		if (this._expr) {
			// we evaluate ignoring any errors...
			JSG.FormulaParser.runIgnoringErrors(() => this._expr.evaluateForSheet(sheet));
		}
	}

	invalidateTerm() {
		if (this._expr) {
			this._expr.invalidateTerm();
		}
	}

	calc(item) {
		if (this._expr) {
			if (item !== undefined) {
				this._expr.evaluate(item);
			}
			this._value = this._expr.getValue();
		} else {
			this._value = undefined;
		}
	}

	saveNeeded() {
		// no need to save
		if (!this.hasFormat() && !this.hasContent()) {
			return false;
		}

		return true;
	}

	/**
	 * Saves this Cell instance to the given Stream.
	 *
	 * @method save
	 * @param {Writer} writer Writer to use for streaming.
	 */
	save(writer) {
		writer.writeStartElement('cell');

		if (this._expr) {
			this._expr.save('value', writer, 15);
		}

		if (this.format) {
			writer.writeStartElement('format');
			writer.writeObject(this.format);
			writer.writeEndElement();
		}
		if (this.textFormat) {
			writer.writeStartElement('textformat');
			writer.writeObject(this.textFormat);
			writer.writeEndElement();
		}
		if (this.attributes) {
			writer.writeStartElement('attributes');
			writer.writeObject(this.attributes);
			writer.writeEndElement();
		}

		writer.writeEndElement();
	}

	/**
	 * Read to initialize this Cell.
	 *
	 * @method read
	 * @param {Reader} reader Reader to use for reading.
	 * @param {Object} object Object to read.
	 */
	read(reader, object) {
		reader.iterateObjects(object, (name, child) => {
			switch (name) {
				case 'value':
					if (!this._expr) {
						this._expr = new Expression(0);
					}
					this._expr.read(reader, child);
					this._value = this._expr.getValue();
					break;
				case 'format':
					this.format = child;
					break;
				case 'textformat':
					this.textFormat = child;
					break;
				case 'attributes':
					this.attributes = child;
					break;
				default:
					break;
			}
		});
	}
};
